import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { postJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';

import { captureHydrationException, getSliceState } from '../helpers';

import { SLICE_NAME } from './constants';
import type {
  JobPosition,
  JobPositionPayload,
  JobPositionSerializedEntity,
  JobPositionsState,
} from './types';

export const jobPositionsAdapter = createEntityAdapter<JobPosition>();

const initialState: JobPositionsState = {
  ...jobPositionsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: jobPositionsAdapter.setAll,
    removeAll: jobPositionsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action.payload?.included) {
        try {
          const jobPositions: JobPosition[] = (
            action.payload.included as JobPositionSerializedEntity[]
          )
            .filter(
              (entity: JobPositionSerializedEntity) =>
                entity.type === 'jobPosition'
            )
            .map((entity: JobPositionSerializedEntity) => ({
              id: entity.id,
              type: entity.type,
              position: entity.attributes.position,
            }));
          jobPositionsAdapter.setAll(state, jobPositions);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });
  },
});

export const { setAll, removeAll } = slice.actions;
export const { reducer } = slice;

export const {
  selectById: selectJobPositionById,
  selectIds: selectJobPositionIds,
  selectEntities: selectJobPositionEntities,
  selectAll: selectAllJobPositions,
  selectTotal: selectTotalJobPositions,
} = jobPositionsAdapter.getSelectors<RootState>(
  state => getSliceState(state)[SLICE_NAME]
);

export const sortJobPositions = createAsyncThunk<
  JobPositionPayload,
  { sort_by: string; ids: string[] }
>(`${SLICE_NAME}/sort`, async payload => {
  const response = await postJSON(
    '/schedule_builder/job_positions/sort',
    payload
  );
  return response;
});
